import React, { useContext, useState } from 'react';
import { useCanvas } from '../../../context/CanvasContext';
import { useEditorUI } from '../../../context/EditorUIContext';
import EditSidebarTabs from './EditSidebarTabs';
import SituationsList from './SituationsList';
import UploadModal from './UploadModal';
import PDFPreviewModal from './PDFPreviewModal';
import textStyles from './styles/EditSidebarText.module.css';
import settingsStyles from './styles/EditSidebarSettings.module.css';
import DeleteModal from './DeleteModal';
import useIsMobile from '../../../utils/useIsMobile';
import StyledSwitch from './StyledSwitch';
import MediaPlayer from '../../../utils/MediaPlayer';
import { getMediaSrc } from '../../../utils/utils';
import TextEditor from '../../texteditor/TextEditor';
import tabStyles from './styles/EditSidebarTabs.module.css';
import AuthContext from '../../../context/AuthContext';
import { FaExpand } from 'react-icons/fa';
import { Tooltip } from 'react-tooltip';
import ScenarioTextEditorModal from './ScenarioTextEditorModal';

/**
 * Edits one situation group.
 *
 * Groups are draft state: every commit here is an `updateGroup` op, so it is
 * optimistic, rebroadcast to collaborators, and invisible to players until the
 * scenario is published.
 *
 * Two things that used to happen here no longer do:
 *   - Membership is not written from this panel. `node.data.situationGroup` is
 *     the only record of it, and publish rebuilds `SituationGroup.situations`
 *     from the draft.
 *   - Changing the colour no longer pushes a fillColor onto every member node.
 *     The colour is resolved from the group at render time, so one op does what
 *     used to take two requests and cannot leave the two out of step.
 *
 * @param {{groupId: string}} props
 */
const EditGroup = ({ groupId, setRenderEditSidebar }) => {
    const { nodes, getGroup, updateGroup, deleteGroup } = useCanvas();
    const { activeEditTab, selectedGroupQueue } = useEditorUI();
    const { userId: authUserId } = useContext(AuthContext);

    const isMobile = useIsMobile();

    const group = getGroup(groupId);

    // Seeded once per group: EditSidebar keys this panel by group id.
    const [title, setTitle] = useState(group?.title || '');
    const [text, setText] = useState(group?.text || '');
    const [audioLabel, setAudioLabel] = useState(group?.audioLabel || '');
    const [color, setColor] = useState(group?.color || '#ffffff');

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [uploadType, setUploadType] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isTextEditorModalOpen, setIsTextEditorModalOpen] = useState(false);
    const [isPDFPreviewOpen, setIsPDFPreviewOpen] = useState(false);
    const [activeSubTab, setActiveSubTab] = useState('text');

    // Membership is a property of the situations, so it is read off the graph.
    const memberNodes = nodes.filter((node) => node.data?.situationGroup === groupId);

    const queue = selectedGroupQueue[groupId];
    const isWaiting = !!queue && queue[0] !== authUserId;

    const commit = (patch) => updateGroup(groupId, patch);

    const openModal = (type) => {
        setUploadType(type);
        setIsModalOpen(true);
    };

    const confirmDeleteGroup = () => {
        deleteGroup(groupId);
        setIsDeleteModalOpen(false);
        if (isMobile) setRenderEditSidebar(false);
    };

    if (!group) return null;
    if (isWaiting) return <div>Waiting</div>;

    return (
        <div className={tabStyles.typeGroup}>
            <EditSidebarTabs editType="group" />

            {activeEditTab === '1' && (
                <>
                    <div className={textStyles.subTabsContainer}>
                        <div
                            className={`${textStyles.subTab} ${
                                activeSubTab === 'text' ? textStyles.activeSubTab : ''
                            }`}
                            onClick={() => setActiveSubTab('text')}
                        >
                            Text
                        </div>
                        <div
                            className={`${textStyles.subTab} ${
                                activeSubTab === 'media' ? textStyles.activeSubTab : ''
                            }`}
                            onClick={() => setActiveSubTab('media')}
                        >
                            Media
                        </div>
                    </div>

                    <div className={textStyles.textContainer}>
                        {activeSubTab === 'text' && (
                            <>
                                <div className={textStyles.titleRow}>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(event) => setTitle(event.target.value)}
                                        onBlur={() => {
                                            if (title !== (group.title || '')) commit({ title });
                                        }}
                                        className={textStyles.titleInput}
                                    />
                                    <button
                                        className={textStyles.expandButton}
                                        onClick={() => setIsTextEditorModalOpen(true)}
                                        data-tooltip-id="preview_tooltip"
                                        data-tooltip-content="Expand Editor"
                                    >
                                        <FaExpand />
                                    </button>
                                </div>
                                <TextEditor
                                    initialData={text}
                                    onChange={setText}
                                    onBlur={(html) => {
                                        if (html !== (group.text || '')) commit({ text: html });
                                    }}
                                />
                            </>
                        )}

                        {activeSubTab === 'media' && (
                            <>
                                {group.image && (
                                    <div className={textStyles.mediaPreview}>
                                        <img
                                            src={group.image}
                                            alt="Uploaded"
                                            className={textStyles.imagePreview}
                                        />
                                    </div>
                                )}

                                <div className={textStyles.buttonGroup}>
                                    <button
                                        onClick={() => openModal('image')}
                                        className={`${textStyles.buttonBase} ${textStyles.uploadButton}`}
                                    >
                                        {group.image ? 'Change Image' : 'Upload Image'}
                                    </button>
                                    {group.image && (
                                        <button
                                            onClick={() => commit({ image: null })}
                                            className={`${textStyles.buttonBase} ${textStyles.removeButton}`}
                                        >
                                            Remove Image
                                        </button>
                                    )}
                                </div>

                                {group.audio && (
                                    <>
                                        <input
                                            type="text"
                                            value={audioLabel}
                                            onChange={(event) => setAudioLabel(event.target.value)}
                                            onBlur={() => {
                                                if (audioLabel !== (group.audioLabel || '')) {
                                                    commit({ audioLabel });
                                                }
                                            }}
                                            className={textStyles.titleInput}
                                        />
                                        <div className={textStyles.mediaPreview}>
                                            <MediaPlayer
                                                src={getMediaSrc(group.audio)}
                                                autoplay={false}
                                                key={getMediaSrc(group.audio)}
                                            />
                                        </div>
                                    </>
                                )}

                                <div className={textStyles.buttonGroup}>
                                    <button
                                        onClick={() => openModal('audiovideo')}
                                        className={`${textStyles.buttonBase} ${textStyles.uploadButton}`}
                                    >
                                        {group.audio ? 'Change Audio/Video' : 'Upload Audio/Video'}
                                    </button>
                                    {group.audio && (
                                        <button
                                            onClick={() => commit({ audio: null })}
                                            className={`${textStyles.buttonBase} ${textStyles.removeButton}`}
                                        >
                                            Remove Audio/Video
                                        </button>
                                    )}
                                </div>

                                {group.document && (
                                    <div className={textStyles.mediaPreview}>
                                        <button
                                            onClick={() => setIsPDFPreviewOpen(true)}
                                            className={`${textStyles.buttonBase} ${textStyles.uploadButton}`}
                                        >
                                            View PDF
                                        </button>
                                    </div>
                                )}
                                <div className={textStyles.buttonGroup}>
                                    <button
                                        onClick={() => openModal('document')}
                                        className={`${textStyles.buttonBase} ${textStyles.uploadButton}`}
                                    >
                                        {group.document ? 'Change PDF' : 'Upload PDF'}
                                    </button>
                                    {group.document && (
                                        <button
                                            onClick={() => commit({ document: null })}
                                            className={`${textStyles.buttonBase} ${textStyles.removeButton}`}
                                        >
                                            Remove PDF
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}

            {activeEditTab === '2' && <SituationsList nodes={memberNodes} />}

            {activeEditTab === '3' && (
                <div className={settingsStyles.settingsContainer}>
                    <div className={settingsStyles.colorPickerContainer}>
                        <label htmlFor="groupColor">Edit Color</label>
                        <input
                            type="color"
                            id="groupColor"
                            value={color}
                            onChange={(event) => setColor(event.target.value)}
                            onBlur={() => {
                                if (color !== (group.color || '#ffffff')) commit({ color });
                            }}
                            className={settingsStyles.colorPicker}
                        />
                    </div>

                    <div className={settingsStyles.settingItem}>
                        <label>Autoplay Media</label>
                        <StyledSwitch
                            onChange={(value) => commit({ autoplay: value })}
                            checked={!!group.autoplay}
                            className={settingsStyles.switch}
                        />
                    </div>

                    <button
                        onClick={() => setIsDeleteModalOpen(true)}
                        className={settingsStyles.deleteButton}
                    >
                        Delete Group
                    </button>
                </div>
            )}

            {/* Modals */}
            {isTextEditorModalOpen && (
                <ScenarioTextEditorModal
                    onClose={() => setIsTextEditorModalOpen(false)}
                    initialText={text}
                    onSave={(textToSave) => {
                        setText(textToSave);
                        commit({ text: textToSave });
                    }}
                    situationTitle={title}
                />
            )}

            {isModalOpen && (
                <UploadModal
                    uploadType={uploadType}
                    target={{ kind: 'group', id: groupId }}
                    setIsModalOpen={setIsModalOpen}
                />
            )}

            {isPDFPreviewOpen && group.document && (
                <PDFPreviewModal
                    url={group.document}
                    onClose={() => setIsPDFPreviewOpen(false)}
                />
            )}

            {isDeleteModalOpen && (
                <DeleteModal
                    onConfirm={confirmDeleteGroup}
                    onCancel={() => setIsDeleteModalOpen(false)}
                    type={'Group'}
                />
            )}

            <Tooltip
                id="preview_tooltip"
                place="left"
                type="dark"
                effect="solid"
                style={{ zIndex: 9999 }}
            />
        </div>
    );
};

export default EditGroup;
